import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | success | failed
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) {
        setStatus("failed");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("success");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch {}
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [refresh]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 text-center">
      <div className="max-w-md" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <Loader2 size={48} className="animate-spin text-gold mx-auto mb-6" />
            <h1 className="font-display text-3xl font-bold uppercase">Confirming payment…</h1>
            <p className="text-white/50 mt-2 text-sm">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 size={56} className="text-gold mx-auto mb-6" />
            <h1 className="font-display text-4xl font-bold uppercase">You're Premium</h1>
            <p className="text-white/60 mt-3 text-sm">
              Welcome to the full network. Every premium original is now unlocked.
            </p>
            <button
              onClick={() => navigate("/")}
              data-testid="payment-continue-btn"
              className="mt-8 bg-white text-black font-semibold px-8 py-3 text-sm uppercase tracking-wider hover:bg-white/90 active:scale-95 transition-all"
            >
              Start Watching
            </button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle size={56} className="text-white/50 mx-auto mb-6" />
            <h1 className="font-display text-3xl font-bold uppercase">Payment not completed</h1>
            <p className="text-white/50 mt-2 text-sm">
              No worries — you can try again anytime.
            </p>
            <button
              onClick={() => navigate("/")}
              data-testid="payment-back-btn"
              className="mt-8 border border-white/25 px-8 py-3 text-sm uppercase tracking-wider hover:bg-white/10 transition-colors"
            >
              Back to Home
            </button>
          </>
        )}
      </div>
    </div>
  );
}
